import StoreScopeTag from "@/components/partner/StoreScopeTag";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Users, Copy, Check, ScrollText } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Iter-325 — Partner Dashboard "Staff & Till" panel (vendor OWNER).
 *   GET/POST /engagement/vendors/me/staff
 *   PUT      /engagement/vendors/me/staff/{staff_id}
 *   GET      /engagement/vendors/me/staff-audit
 * Staff use {site}/till with the vendor ID + their 4-digit PIN.
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

const PERMISSION_LABELS = {
  operate_till: "Operate till",
  view_sessions: "View sessions",
  view_earnings_ledger: "View earnings",
  edit_discount_settings: "Edit discount settings",
  view_kyc_documents: "View KYC documents",
  manage_staff_accounts: "Manage staff",
  // Iter 685 — staff can decide customer review posts from the till.
  approve_reviews: "Approve / reject customer reviews at the till",
};

function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string") return d.message;
  return fallback;
}

export default function StaffTillPanel({ vendorId }) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [copied, setCopied] = useState(false);

  const staffQ = useQuery({
    queryKey: ["partner-staff"],
    retry: 0,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/staff", OPTS)).data,
  });
  const staff = staffQ.data?.staff || [];
  const catalog = staffQ.data?.permissions_catalog || Object.keys(PERMISSION_LABELS);

  const tillLink = `${window.location.origin}/till`;
  const copyTill = async () => {
    try {
      await navigator.clipboard.writeText(tillLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 text-left" data-testid="partner-staff-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          <Users className="w-4 h-4" /> Staff &amp; till
          <StoreScopeTag />
        </div>
        <button
          type="button"
          data-testid="partner-staff-add-toggle"
          onClick={() => setShowAdd((v) => !v)}
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-1.5 transition-colors"
        >
          {showAdd ? "Close" : "Add staff"}
        </button>
      </div>

      <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600">
        Give staff this link: <span className="font-bold text-slate-800">{tillLink}</span>
        <button
          type="button"
          onClick={copyTill}
          data-testid="partner-till-link-copy"
          className="ml-2 inline-flex items-center gap-1 font-bold text-[color:var(--brand-blue)] hover:underline"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <div className="mt-1">
          They log in with your vendor ID <span className="font-bold text-slate-800">{vendorId}</span> + their PIN.
        </div>
      </div>

      {showAdd && (
        <AddStaffForm
          catalog={catalog}
          onDone={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ["partner-staff"] });
          }}
        />
      )}

      {staffQ.isLoading ? (
        <div className="mt-4 grid place-items-center py-6 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : staff.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="partner-staff-empty">
          No staff yet — add your first till operator above.
        </p>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="partner-staff-list">
          {staff.map((m) => (
            <StaffRow key={m.id} member={m} onChanged={() => qc.invalidateQueries({ queryKey: ["partner-staff"] })} />
          ))}
        </ul>
      )}

      <button
        type="button"
        data-testid="partner-staff-audit-toggle"
        onClick={() => setShowAudit((v) => !v)}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800"
      >
        <ScrollText className="w-3.5 h-3.5" /> {showAudit ? "Hide activity" : "View activity"}
      </button>
      {showAudit && <AuditTrail />}
    </div>
  );
}

function AddStaffForm({ catalog, onDone }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [pin, setPin] = useState("");
  const [perms, setPerms] = useState({ operate_till: true });

  const addMut = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), pin, permissions: perms };
      if (contact.trim()) body.contact = contact.trim();
      return (await sharedApi.post("/classifieds/engagement/vendors/me/staff", body, OPTS)).data;
    },
    onSuccess: (data) => {
      toast.success(`${data?.name || "Staff member"} added.`);
      onDone();
    },
    onError: (err) => toast.error(errMsg(err, "Couldn't add staff member")),
  });

  return (
    <form
      className="mt-3 rounded-xl border border-slate-200 p-4 space-y-3"
      data-testid="partner-staff-add-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim().length < 2) return toast.error("Name must be at least 2 characters.");
        if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits.");
        addMut.mutate();
      }}
    >
      <div className="grid sm:grid-cols-3 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" maxLength={80}
          data-testid="partner-staff-name" className="rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:border-slate-500" />
        <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact (optional)"
          data-testid="partner-staff-contact" className="rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:border-slate-500" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit PIN" inputMode="numeric"
          data-testid="partner-staff-pin" className="rounded-full border border-slate-300 px-4 py-2 text-sm tracking-widest focus:outline-none focus:border-slate-500" />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {catalog.map((k) => (
          <label key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={!!perms[k]}
              onChange={(e) => setPerms((p) => ({ ...p, [k]: e.target.checked }))}
              data-testid={`partner-staff-perm-${k}`}
            />
            {PERMISSION_LABELS[k] || k.replace(/_/g, " ")}
          </label>
        ))}
      </div>
      <button
        type="submit"
        disabled={addMut.isPending}
        data-testid="partner-staff-add-submit"
        className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-5 py-2 disabled:opacity-60 transition-colors"
      >
        {addMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add staff member"}
      </button>
    </form>
  );
}

function StaffRow({ member: m, onChanged }) {
  const [pinEdit, setPinEdit] = useState(false);
  const [newPin, setNewPin] = useState("");
  const disabled = m.status === "disabled";

  const updateMut = useMutation({
    mutationFn: async (body) =>
      (await sharedApi.put(`/classifieds/engagement/vendors/me/staff/${m.id}`, body, OPTS)).data,
    onSuccess: () => {
      toast.success("Staff member updated.");
      setPinEdit(false);
      setNewPin("");
      onChanged();
    },
    onError: (err) => toast.error(errMsg(err, "Couldn't update staff member")),
  });

  const activePerms = Object.entries(m.permissions || {}).filter(([, v]) => v).map(([k]) => k);

  return (
    <li className="rounded-xl border border-slate-200 px-4 py-3" data-testid={`partner-staff-row-${m.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`font-bold text-sm ${disabled ? "text-slate-400 line-through" : "text-slate-900"}`}>{m.name}</span>
        <span className={`rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${
          disabled ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
        }`}>
          {disabled ? "Disabled" : "Active"}
        </span>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <button type="button" onClick={() => setPinEdit((v) => !v)} data-testid={`partner-staff-pin-btn-${m.id}`}
            className="font-bold text-slate-500 hover:text-slate-800">Change PIN</button>
          <button
            type="button"
            data-testid={`partner-staff-toggle-${m.id}`}
            onClick={() => updateMut.mutate({ status: disabled ? "active" : "disabled" })}
            disabled={updateMut.isPending}
            className={`font-bold ${disabled ? "text-emerald-600 hover:text-emerald-700" : "text-red-500 hover:text-red-600"} disabled:opacity-60`}
          >
            {disabled ? "Enable" : "Disable"}
          </button>
        </div>
      </div>
      {activePerms.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {activePerms.map((k) => (
            <span key={k} className="rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold px-2 py-0.5">
              {PERMISSION_LABELS[k] || k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}
      {pinEdit && (
        <form
          className="mt-2 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be exactly 4 digits.");
            updateMut.mutate({ pin: newPin });
          }}
        >
          <input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="New 4-digit PIN" inputMode="numeric" data-testid={`partner-staff-newpin-${m.id}`}
            className="rounded-full border border-slate-300 px-4 py-1.5 text-xs tracking-widest focus:outline-none focus:border-slate-500" />
          <button type="submit" disabled={updateMut.isPending} data-testid={`partner-staff-newpin-save-${m.id}`}
            className="rounded-full bg-slate-900 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-60">
            Save
          </button>
        </form>
      )}
    </li>
  );
}

function AuditTrail() {
  const q = useQuery({
    queryKey: ["partner-staff-audit"],
    retry: 0,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/staff-audit", OPTS)).data,
  });
  const rows = q.data?.audit || (Array.isArray(q.data) ? q.data : []);
  if (q.isLoading) return <div className="mt-2 text-xs text-slate-400">Loading activity…</div>;
  if (rows.length === 0) return <div className="mt-2 text-xs text-slate-400" data-testid="partner-staff-audit-empty">No activity yet.</div>;
  return (
    <ul className="mt-2 space-y-1 text-xs text-slate-600 max-h-56 overflow-y-auto" data-testid="partner-staff-audit-list">
      {rows.map((r, i) => (
        <li key={r.id || i} className="rounded-lg bg-slate-50 px-3 py-1.5">
          <span className="font-semibold capitalize">{r.actor_type || "—"}</span>{" "}
          {(r.action || "").replace(/_/g, " ")}
          {r.detail ? ` — ${r.detail}` : ""}
          {r.at && (
            <span className="text-slate-400"> · {new Date(r.at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
